import { useMutation, DocumentNode } from '@apollo/client';
import printGraphqlErrors from '../../utils/apollo/printGraphqlErrors';
import UpdateTracker from '../../utils/analytics/UpdateTracker';
import {
  IEntityCategoryFieldsFragment,
  IManageBasicEventMutationVariables,
  IManageFieldMutationVariables,
} from '../../utils/apollo/graphqlSchema';
import {
  INamedCohortAddCategoryMutation,
  INamedCohortRemoveCategoryMutation,
  INamedMeasureAddCategoryMutation,
  INamedMeasureRemoveCategoryMutation,
  IVirtualEventAddCategoryMutation,
  IVirtualEventRemoveCategoryMutation,
  INamedFilterAddCategoryMutation,
  INamedFilterRemoveCategoryMutation,
  INamedBucketDefAddCategoryMutation,
  INamedBucketDefRemoveCategoryMutation,
  IFieldAddCategoryMutation,
  IFieldRemoveCategoryMutation,
  IBasicEventAddCategoryMutation,
  IBasicEventRemoveCategoryMutation,
  IManageVersionEntityMutation,
  IManageFieldMutation,
  IManageBasicEventMutation,
  ManagedEntityType,
} from '../../utils/apollo/graphqlSchema';
import {
  CategoryActionType,
  CategoryEntityType,
  formatEntityCategoryErrors,
  generateTagActions,
} from '../../components/Categories/utils';
import { createDictionaryMutationInput } from './utils';
import {
  MANAGE_BASIC_EVENT,
  MANAGE_FIELD,
  MANAGE_VERSION_ENTITY,
} from '../../utils/apollo/manageView/mutations';

type AddCategoryMutation =
  | INamedCohortAddCategoryMutation
  | INamedMeasureAddCategoryMutation
  | IVirtualEventAddCategoryMutation
  | INamedFilterAddCategoryMutation
  | INamedBucketDefAddCategoryMutation
  | IFieldAddCategoryMutation
  | IBasicEventAddCategoryMutation;

type RemoveCategoryMutation =
  | INamedCohortRemoveCategoryMutation
  | INamedMeasureRemoveCategoryMutation
  | IVirtualEventRemoveCategoryMutation
  | INamedFilterRemoveCategoryMutation
  | INamedBucketDefRemoveCategoryMutation
  | IFieldRemoveCategoryMutation
  | IBasicEventRemoveCategoryMutation;

interface BaseMutationInput {
  input: {
    entityIds?: string[];
    basicEventKeys?: string[];
    fieldKeys?: string[];
    categoryIds: string[];
    schema?: string;
  };
}

interface ManageMutationInput {
  input: Array<{
    entityId: string;
    entityType: string;
    isManaged: boolean;
  }>;
}

interface BaseApplyTagsOptions<T> {
  entityType: CategoryEntityType;
  managedEntityType?: string;
  addCategoryMutation: DocumentNode;
  removeCategoryMutation: DocumentNode;
  getEntityId: (entity: T) => string;
  getEntityCategories: (entity: T) => IEntityCategoryFieldsFragment[];
  onSuccess?: (entities: T[]) => void;
}

export const useApplyTags = <T>({
  entityType,
  managedEntityType,
  addCategoryMutation,
  removeCategoryMutation,
  getEntityId,
  getEntityCategories,
  onSuccess,
}: BaseApplyTagsOptions<T>) => {
  const [addCategory] = useMutation<AddCategoryMutation, BaseMutationInput>(
    addCategoryMutation,
  );
  const [removeCategory] = useMutation<
    RemoveCategoryMutation,
    BaseMutationInput
  >(removeCategoryMutation);
  const [manageEntity] = useMutation<
    IManageVersionEntityMutation,
    ManageMutationInput
  >(MANAGE_VERSION_ENTITY);
  const [manageField] = useMutation<
    IManageFieldMutation,
    IManageFieldMutationVariables
  >(MANAGE_FIELD);
  const [manageBasicEvent] = useMutation<
    IManageBasicEventMutation,
    IManageBasicEventMutationVariables
  >(MANAGE_BASIC_EVENT);

  return async (args: {
    entities: T[];
    newTags: string[];
    isManaged: boolean;
    schemaId?: string;
  }) => {
    const { entities, newTags, isManaged, schemaId } = args;
    const updateTracker = new UpdateTracker();

    if (entities.length === 1) {
      // Single entity - allow tag removal
      const entity = entities[0];
      const { tagsToAdd, tagsToRemove } = generateTagActions({
        existingTags: getEntityCategories(entity),
        newTags,
      });

      if (tagsToRemove.length) {
        const response = await removeCategory({
          variables: {
            input: createDictionaryMutationInput(
              entityType,
              [getEntityId(entity)],
              tagsToRemove,
              schemaId,
            ),
          },
        });
        updateTracker.captureError(
          formatEntityCategoryErrors({
            entityType,
            data: response.data,
            actionType: CategoryActionType.Remove,
          }),
        );
      }

      if (tagsToAdd.length) {
        const response = await addCategory({
          variables: {
            input: createDictionaryMutationInput(
              entityType,
              [getEntityId(entity)],
              tagsToAdd,
              schemaId,
            ),
          },
        });
        updateTracker.captureError(
          formatEntityCategoryErrors({
            entityType,
            data: response.data,
            actionType: CategoryActionType.Add,
          }),
        );
      }
    } else {
      // Multiple entities - only add tags
      const { tagsToAdd } = generateTagActions({
        existingTags: [],
        newTags,
      });

      const response = await addCategory({
        variables: {
          input: createDictionaryMutationInput(
            entityType,
            entities.map(getEntityId),
            tagsToAdd,
            schemaId,
          ),
        },
      });
      updateTracker.captureError(
        formatEntityCategoryErrors({
          entityType,
          data: response.data,
          actionType: CategoryActionType.Add,
        }),
      );
    }

    if (managedEntityType === ManagedEntityType.Field && schemaId) {
      const res = await manageField({
        variables: {
          input: entities.map((entity) => ({
            schema: schemaId,
            fieldKey: getEntityId(entity),
            isManaged,
          })),
        },
      });

      if (res.data?.manageField?.errors) {
        updateTracker.captureError(
          printGraphqlErrors(res.data.manageField.errors),
        );
      }
    } else if (managedEntityType === ManagedEntityType.BasicEvent && schemaId) {
      const res = await manageBasicEvent({
        variables: {
          input: entities.map((entity) => ({
            schema: schemaId,
            eventKey: getEntityId(entity),
            isManaged,
          })),
        },
      });

      if (res.data?.manageBasicEvent?.errors) {
        updateTracker.captureError(
          printGraphqlErrors(res.data.manageBasicEvent.errors),
        );
      }
    } else {
      const res = await manageEntity({
        variables: {
          input: entities.map((entity) => ({
            entityId: getEntityId(entity),
            entityType: managedEntityType as string,
            isManaged,
          })),
        },
      });

      if (res.data?.manageVersionEntity?.errors) {
        updateTracker.captureError(
          printGraphqlErrors(res.data.manageVersionEntity.errors),
        );
      }
    }

    if (updateTracker.hasSuccessfulUpdate() && onSuccess) {
      onSuccess(entities);
    }
  };
};

export default useApplyTags;
